/**
 * useQuery — the list/detail fetch primitive for admin screens (§14.3):
 * skeleton while loading, typed error with retry, race-safe on fast filter
 * changes (only the LATEST request may settle state), refetch via `reload`.
 *
 * Deliberately small: server-side pagination (§11.2) means screens never
 * need caches or background sync here — every state change refetches a page.
 */

import { useCallback, useEffect, useRef, useState } from "react";

export interface QueryState<T> {
  readonly data: T | null;
  readonly loading: boolean;
  readonly error: unknown;
  readonly reload: () => void;
}

export function useQuery<T>(fetcher: () => Promise<T>, key: string): QueryState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [tick, setTick] = useState(0);

  // Latest-ref: `key` decides WHEN to fetch; the fetcher closure itself is
  // rebuilt every render and must not retrigger the effect.
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const requestSerial = useRef(0);

  useEffect(() => {
    const serial = ++requestSerial.current;
    setLoading(true);
    setError(null);
    fetcherRef
      .current()
      .then((result) => {
        if (requestSerial.current !== serial) return; // superseded
        setData(result);
        setLoading(false);
      })
      .catch((cause: unknown) => {
        if (requestSerial.current !== serial) return;
        setError(cause);
        setLoading(false);
      });
  }, [key, tick]);

  const reload = useCallback(() => {
    setTick((value) => value + 1);
  }, []);

  return { data, loading, error, reload };
}
